export const STUDIO_AGENT_OPERATIONS = Object.freeze([
  'upsert_component', 'remove_component', 'detach_component', 'apply_subject', 'list_narratives', 'write_narrative',
  'inspect', 'get_document', 'create_document', 'replace_document', 'set_title', 'add_section',
  'select_section', 'switch_mode', 'update_section', 'insert_text', 'update_text', 'add_shape', 'add_image',
  'update_element', 'select_elements', 'undo', 'redo', 'save_local', 'save_local_copy', 'list_drafts', 'read_draft', 'open_draft',
  'preview', 'import_penpot', 'learning_targets', 'content_items', 'set_editing_view', 'select_content', 'update_content',
  'design_shapes', 'create_design_shape', 'update_design_shape', 'penpot_mcp',
  'list_assets', 'register_asset', 'insert_asset', 'list_templates', 'save_template', 'open_template', 'delete_template', 'set_template_scope',
]);
const READ_OPERATIONS = new Set(['inspect', 'get_document', 'list_drafts', 'read_draft', 'learning_targets', 'content_items', 'design_shapes', 'list_assets', 'list_templates', 'list_narratives']);
export const agentError = (code, message) => Object.assign(new Error(message), { code });
export function agentObject(value, name = 'input') {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw agentError('INVALID_INPUT', `${name}은 객체여야 합니다.`);
  return value;
}
export function agentString(value, name, max = 200) {
  if (typeof value !== 'string' || value.length > max) throw agentError('INVALID_INPUT', `${name}은 ${max}자 이하의 문자열이어야 합니다.`);
  return value;
}

/** Forward original tool arguments. Never place the editor's token in agent results/errors. */
export async function callPenpotMcp({ userToken, fileId, pageId, tool, arguments: args }, fetchImpl = globalThis.fetch.bind(globalThis)) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 65000);
  try {
    const response = await fetchImpl('/__studio/agent/penpot', {
      method: 'POST', credentials: 'omit', cache: 'no-store', redirect: 'error', signal: controller.signal,
      headers: { 'Content-Type': 'application/json', 'X-EduSense-Studio': '1' },
      body: JSON.stringify({ userToken, fileId, pageId, tool, arguments: args }),
    });
    if (!response.ok) throw new Error('relay failed');
    const result = await response.json();
    if (!Array.isArray(result?.content)) throw new Error('invalid upstream result');
    return result;
  } catch {
    throw agentError('PENPOT_MCP_UNAVAILABLE', 'Penpot 원본 MCP 응답을 확인하지 못했습니다. 실행 여부를 확인한 뒤 다시 요청하세요.');
  } finally { clearTimeout(timer); }
}

/** Retries reuse the result, including failures, rather than applying an edit twice. */
export function createAgentCommandQueue({ getGeneration, execute, waitUntilReady = async () => {} }) {
  let tail = Promise.resolve(), disposed = false;
  const requests = new Map();
  function command(operation, input = {}) {
    if (!STUDIO_AGENT_OPERATIONS.includes(operation)) return Promise.reject(agentError('UNKNOWN_OPERATION', '지원하지 않는 제작실 명령입니다.'));
    try { input = structuredClone(agentObject(input)); } catch (error) { return Promise.reject(error); }
    const mutation = !READ_OPERATIONS.has(operation);
    let signature;
    if (mutation) {
      if (typeof input.requestId !== 'string' || !/^[a-zA-Z0-9_-]{1,100}$/.test(input.requestId)) return Promise.reject(agentError('INVALID_REQUEST_ID', '변경 명령에는 고유한 requestId가 필요합니다.'));
      if (!Number.isSafeInteger(input.expectedGeneration) || input.expectedGeneration < 0) return Promise.reject(agentError('INVALID_GENERATION', 'inspect의 generation을 expectedGeneration으로 전달해 주세요.'));
      signature = JSON.stringify([operation, input]);
      const previous = requests.get(input.requestId);
      if (previous) return previous.signature === signature ? previous.result.then(structuredClone) : Promise.reject(agentError('REQUEST_ID_CONFLICT', '같은 requestId를 다른 명령에 사용할 수 없습니다.'));
      if (requests.size >= 2048) return Promise.reject(agentError('SESSION_COMMAND_LIMIT', '명령 기록 한도에 도달했습니다. 저장한 뒤 제작실을 다시 열어 주세요.'));
    }
    const result = tail.catch(() => {}).then(async () => {
      if (disposed) throw agentError('CLOSED', '닫힌 편집기입니다.');
      await waitUntilReady();
      if (disposed) throw agentError('CLOSED', '닫힌 편집기입니다.');
      if (mutation && input.expectedGeneration !== getGeneration()) throw agentError('STALE_GENERATION', '콘텐츠가 변경되었습니다. inspect로 최신 상태를 확인한 뒤 새 requestId로 다시 요청하세요.');
      return execute(operation, structuredClone(input), { mutation });
    });
    tail = result;
    if (mutation) requests.set(input.requestId, { signature, result });
    return result.then(structuredClone);
  }
  return { command, dispose() { disposed = true; } };
}

export function studioBrokerOrigin(value) {
  let url;
  try { url = new URL(value); } catch { throw agentError('INVALID_BROKER', '로컬 MCP 서버 주소를 입력해 주세요.'); }
  if (!['127.0.0.1', 'localhost', '[::1]'].includes(url.hostname) || url.protocol !== 'http:' || url.username || url.password || url.search || url.hash || url.pathname !== '/') {
    throw agentError('INVALID_BROKER', 'MCP 연결은 http://localhost 또는 http://127.0.0.1의 로컬 서버만 지원합니다.');
  }
  return url.origin;
}

/** Same-origin local transport keeps the broker credential on the preview server. */
export function mountAgentConnection({ execute, getLabel, fetchImpl = window.fetch.bind(window) }) {
  const hostWindow = window;
  const panel = document.createElement('div'); panel.className = 'es-agent-connection';
  const status = document.createElement('span'); status.className = 'es-agent-status'; status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
  const reconnect = document.createElement('button'); reconnect.type = 'button'; reconnect.textContent = '재연결'; reconnect.dataset.testid = 'studio-agent-connect'; reconnect.hidden = true;
  panel.append(status, reconnect);
  const prefix = '/__studio/agent';
  const headers = { 'X-EduSense-Studio': '1' };
  let active = null, disposed = false, paused = false, retryTimer = null, failures = 0;
  function show(state, message) { panel.dataset.state = state; status.textContent = message; reconnect.hidden = state !== 'offline' && state !== 'disconnected'; }
  function clearRetry() { if (retryTimer !== null) { clearTimeout(retryTimer); retryTimer = null; } }
  async function request(connection, path, init = {}) {
    const response = await fetchImpl(`${prefix}${path}`, { ...init, credentials: 'omit', cache: 'no-store', redirect: 'error', signal: connection.abort.signal, headers: { ...headers, ...(init.body ? { 'Content-Type': 'application/json' } : {}) } });
    if (!response.ok) throw agentError('BROKER_REQUEST_FAILED', `제작 도우미 연결을 기다리는 중 (${response.status})`);
    return response.status === 204 ? null : response.json();
  }
  async function release(connection) {
    if (!connection.sessionId) return;
    const abort = new AbortController(); const timer = setTimeout(() => abort.abort(), 3000);
    try { await fetchImpl(`${prefix}/sessions/${connection.sessionId}`, { method: 'DELETE', credentials: 'omit', cache: 'no-store', redirect: 'error', keepalive: true, signal: abort.signal, headers }); }
    catch { /* The broker expires disconnected tabs even when the server is unavailable. */ }
    finally { clearTimeout(timer); }
  }
  async function stop(message = '제작 도우미 연결이 해제됨') {
    paused = true; clearRetry();
    const connection = active; active = null;
    if (connection) { connection.abort.abort(); await release(connection); }
    if (!disposed && !active) show('disconnected', message);
  }
  async function start() {
    if (active || disposed || paused) return;
    clearRetry();
    try { studioBrokerOrigin(hostWindow.location.origin); }
    catch { show('unavailable', '로컬 제작 도우미 연결 없음'); return; }
    const connection = { abort: new AbortController(), sessionId: null }; active = connection;
    show('connecting', '제작 도우미 연결 중…');
    try {
      const registered = await request(connection, '/sessions', { method: 'POST', body: JSON.stringify({ label: getLabel(), capabilities: [...STUDIO_AGENT_OPERATIONS] }) });
      if (typeof registered?.sessionId !== 'string' || !/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/.test(registered.sessionId)) throw agentError('INVALID_SESSION', '제작 도우미 연결을 다시 확인하고 있습니다.');
      connection.sessionId = registered.sessionId;
      if (active !== connection || disposed) { await release(connection); return; }
      failures = 0; show('connected', '제작 도우미 연결됨');
      while (active === connection && !disposed) {
        const reply = await request(connection, `/sessions/${connection.sessionId}/commands`);
        if (active !== connection || disposed) return;
        if (reply.command === null) continue;
        const command = reply.command;
        if (!command || typeof command.id !== 'string' || typeof command.operation !== 'string') throw agentError('INVALID_COMMAND', '제작 도우미 연결을 다시 확인하고 있습니다.');
        let result;
        try { result = { id: command.id, result: await execute(command.operation, command.input) }; }
        catch (error) { result = { id: command.id, error: { code: typeof error.code === 'string' ? error.code : 'COMMAND_FAILED', message: error.message } }; }
        if (active !== connection || disposed) return;
        await request(connection, `/sessions/${connection.sessionId}/results`, { method: 'POST', body: JSON.stringify(result) });
      }
    } catch (error) {
      if (active !== connection || disposed) return;
      active = null; connection.abort.abort();
      show('offline', '제작 도우미 연결 대기 중');
      await release(connection);
      if (disposed || paused || active) return;
      retryTimer = setTimeout(() => { retryTimer = null; void start(); }, Math.min(30000, 1000 * 2 ** Math.min(failures++, 5)));
    }
  }
  const resume = () => { paused = false; void start(); };
  const pagehide = () => { void stop(); };
  const pageshow = event => { if (event.persisted) resume(); };
  reconnect.addEventListener('click', resume);
  hostWindow.addEventListener('pagehide', pagehide);
  hostWindow.addEventListener('pageshow', pageshow);
  show('connecting', '제작 도우미 연결 중…');
  queueMicrotask(() => { void start(); });
  return { element: panel, disconnect: stop, dispose() { disposed = true; hostWindow.removeEventListener('pagehide', pagehide); hostWindow.removeEventListener('pageshow', pageshow); void stop(); panel.remove(); } };
}
